import { CollectionData, PaginationResults } from "@/lib/type";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import { Supplier } from "./inventory.type";

export interface SupplierWritePayload {
  name: string;
  contactName?: string | null;
  phone?: string | null;
  email?: string | null;
  paymentTerms?: string | null;
  leadTimeDays?: number | null;
  notes?: string | null;
}

export interface GetSuppliersParams {
  page?: number;
  limit?: number;
  search?: string;
  pagination?: boolean;
}

export const SupplierApi = createApi({
  reducerPath: "supplierApi",
  tagTypes: ["supplier"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getSuppliers: build.query<
      PaginationResults<Supplier>,
      { restaurantId: string; query?: GetSuppliersParams }
    >({
      query: ({ restaurantId, query }) => ({
        url: `restaurants/${restaurantId}/suppliers`,
        method: "GET",
        params: query,
      }),
      providesTags: ["supplier"],
      transformResponse: (data: CollectionData<Supplier>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    getSupplier: build.query<Supplier, { supplierUuid: string }>({
      query: ({ supplierUuid }) => `suppliers/${supplierUuid}`,
      providesTags: ["supplier"],
    }),

    createSupplier: build.mutation<
      Supplier,
      { restaurantId: string; data: SupplierWritePayload }
    >({
      query: ({ restaurantId, data }) => ({
        url: `restaurants/${restaurantId}/suppliers`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["supplier"],
    }),

    updateSupplier: build.mutation<
      Supplier,
      { supplierUuid: string; data: Partial<SupplierWritePayload> }
    >({
      query: ({ supplierUuid, data }) => ({
        url: `suppliers/${supplierUuid}`,
        method: "PATCH",
        headers: { "Content-Type": "application/merge-patch+json" },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["supplier"],
    }),

    deleteSupplier: build.mutation<void, { supplierUuid: string }>({
      query: ({ supplierUuid }) => ({
        url: `suppliers/${supplierUuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["supplier"],
    }),
  }),
});

export const {
  useGetSuppliersQuery,
  useGetSupplierQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
  useDeleteSupplierMutation,
} = SupplierApi;
